import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Money, CreditCard, Globe, ChartLineUp, Printer, CheckCircle,
    ArrowsClockwise, WarningCircle, X, DownloadSimple,
} from "@phosphor-icons/react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, authHeaders } from "@/lib/api";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Line, ComposedChart,
} from "recharts";

// Today's date in America/New_York — matches the deli's operating tz so a
// midnight report matches the register.
function todayET() {
    const now = new Date();
    const fmt = new Intl.DateTimeFormat("en-CA", {
        timeZone: "America/New_York",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });
    return fmt.format(now); // YYYY-MM-DD
}

const CH = {
    cash: {
        label: "Efectivo",
        icon: Money,
        gradient: "from-emerald-500/10 to-emerald-500/0",
        text: "text-emerald-700",
        chip: "bg-emerald-500 text-white",
    },
    card: {
        label: "Tarjeta (tienda)",
        icon: CreditCard,
        gradient: "from-blue-500/10 to-blue-500/0",
        text: "text-blue-700",
        chip: "bg-blue-500 text-white",
    },
    online: {
        label: "Venta en línea",
        icon: Globe,
        gradient: "from-primary/10 to-primary/0",
        text: "text-primary",
        chip: "bg-primary text-primary-foreground",
    },
};

// Presets for quick range selection — each returns [from, to] YYYY-MM-DD.
function rangeToday() {
    const d = todayET();
    return [d, d];
}
function rangeThisWeek() {
    // Monday → today
    const today = new Date(todayET() + "T12:00:00");
    const dow = (today.getDay() + 6) % 7; // 0 = Monday
    const start = new Date(today);
    start.setDate(today.getDate() - dow);
    const fmt = (d) => d.toISOString().slice(0, 10);
    return [fmt(start), todayET()];
}
function rangeThisMonth() {
    const t = todayET();
    return [t.slice(0, 8) + "01", t];
}

export default function DailyReportDialog({ open, onClose }) {
    // Modes: 'day' (single-date + cash close) vs 'range' (from..to summary).
    const [mode, setMode] = useState("day");
    const [date, setDate] = useState(todayET());
    const [fromDate, setFromDate] = useState(() => rangeThisWeek()[0]);
    const [toDate, setToDate] = useState(() => rangeThisWeek()[1]);
    const [report, setReport] = useState(null);
    const [rangeReport, setRangeReport] = useState(null);
    const [loading, setLoading] = useState(false);
    // Cash close form
    const [opening, setOpening] = useState("0");
    const [counted, setCounted] = useState("0");
    const [payouts, setPayouts] = useState("0");
    const [notes, setNotes] = useState("");
    const [closing, setClosing] = useState(false);

    const load = useCallback(async (d) => {
        setLoading(true);
        try {
            const r = await api.get("/admin/pos/daily-report", {
                headers: authHeaders(),
                params: { date: d },
            });
            setReport(r.data);
            const lc = r.data.latest_close;
            if (lc) {
                setOpening(String(lc.opening_cash ?? 0));
                setCounted(String(lc.counted_cash ?? 0));
                setPayouts(String(lc.payouts ?? 0));
                setNotes(lc.notes || "");
            } else {
                setOpening("0"); setCounted("0"); setPayouts("0"); setNotes("");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load report");
        } finally { setLoading(false); }
    }, []);

    const loadRange = useCallback(async (from, to) => {
        setLoading(true);
        try {
            const r = await api.get("/admin/pos/range-report", {
                headers: authHeaders(),
                params: { date_from: from, date_to: to },
            });
            setRangeReport(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load range");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        if (!open) return;
        if (mode === "day") load(date);
        else loadRange(fromDate, toDate);
    }, [open, mode, date, fromDate, toDate, load, loadRange]);

    const expected = useMemo(() => {
        const cashSales = report?.summary?.cash?.total || 0;
        return Number(opening || 0) + cashSales - Number(payouts || 0);
    }, [report, opening, payouts]);

    const variance = useMemo(() => Number(counted || 0) - expected, [counted, expected]);

    const close = async () => {
        setClosing(true);
        try {
            await api.post(
                "/admin/pos/cash-close",
                {
                    date,
                    opening_cash: Number(opening || 0),
                    counted_cash: Number(counted || 0),
                    payouts: Number(payouts || 0),
                    notes,
                },
                { headers: authHeaders() },
            );
            toast.success("Caja cerrada");
            load(date);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        } finally { setClosing(false); }
    };

    const doPrint = () => {
        // Uses the browser's native print dialog — the receipt-printer profile
        // was designed with the same document width so this looks identical on
        // thermal paper via AirPrint / BT-emulated OS printers.
        window.print();
    };

    const downloadCSV = () => {
        const isRange = mode === "range";
        const src = isRange ? rangeReport : report;
        if (!src) return;
        const esc = (v) => {
            const s = String(v ?? "");
            if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
            return s;
        };
        const rows = [];
        rows.push(["J & W Deli — Reporte de ventas"]);
        if (isRange) {
            rows.push(["Desde", src.date_from, "Hasta", src.date_to]);
        } else {
            rows.push(["Fecha", src.date]);
        }
        rows.push([]);
        rows.push(["Canal", "Órdenes", isRange ? "" : "Subtotal", isRange ? "" : "Impuesto", "Total"]);
        for (const [k, s] of Object.entries(src.summary)) {
            rows.push([
                CH[k]?.label || k,
                s.count,
                isRange ? "" : s.subtotal.toFixed(2),
                isRange ? "" : s.tax.toFixed(2),
                s.total.toFixed(2),
            ]);
        }
        rows.push([
            "TOTAL",
            src.grand.count,
            isRange ? "" : src.grand.subtotal.toFixed(2),
            isRange ? "" : src.grand.tax.toFixed(2),
            src.grand.total.toFixed(2),
        ]);
        if (isRange && Array.isArray(src.days)) {
            rows.push([]);
            rows.push(["Desglose diario"]);
            rows.push(["Fecha", "Órdenes", "Efectivo", "Tarjeta", "Online", "Total"]);
            for (const d of src.days) {
                rows.push([
                    d.date,
                    d.count,
                    d.cash.toFixed(2),
                    d.card.toFixed(2),
                    d.online.toFixed(2),
                    d.total.toFixed(2),
                ]);
            }
        }
        if (!isRange && report?.latest_close) {
            const lc = report.latest_close;
            rows.push([]);
            rows.push(["Cuadre de caja"]);
            rows.push(["Efectivo inicial", lc.opening_cash]);
            rows.push(["Ventas efectivo", lc.cash_sales]);
            rows.push(["Salidas", lc.payouts]);
            rows.push(["Esperado", lc.expected_cash]);
            rows.push(["Contado", lc.counted_cash]);
            rows.push(["Diferencia", lc.variance]);
            if (lc.notes) rows.push(["Notas", lc.notes]);
            rows.push(["Cerrado por", lc.closed_by]);
            rows.push(["Cerrado a las", new Date(lc.closed_at).toLocaleString()]);
        }
        if (!isRange && Object.keys(report?.by_cashier || {}).length) {
            rows.push([]);
            rows.push(["Por cajero"]);
            rows.push(["Cajero", "Órdenes", "Efectivo", "Total"]);
            for (const [email, row] of Object.entries(report.by_cashier)) {
                rows.push([email, row.count, row.cash.toFixed(2), row.total.toFixed(2)]);
            }
        }
        if (!isRange && Array.isArray(report?.orders) && report.orders.length) {
            rows.push([]);
            rows.push(["Pedidos (más recientes)"]);
            rows.push(["ID", "Hora", "Cliente", "Canal", "Método", "Fulfillment", "Total"]);
            for (const o of report.orders) {
                rows.push([
                    (o.id || "").slice(-8).toUpperCase(),
                    new Date(o.created_at).toLocaleString(),
                    o.customer_name || "",
                    o.channel || "",
                    o.payment_method || "",
                    o.fulfillment || "",
                    Number(o.total || 0).toFixed(2),
                ]);
            }
        }
        const csv = "\uFEFF" + rows.map((r) => r.map(esc).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const suffix = isRange ? `${src.date_from}_${src.date_to}` : src.date;
        a.download = `jw-deli-report-${suffix}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("CSV descargado");
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                data-testid="daily-report-dialog"
                className="max-w-3xl w-[95vw] max-h-[92vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="font-display font-black text-2xl flex items-center gap-2">
                        <ChartLineUp size={24} weight="fill" className="text-primary" />
                        Reporte del día
                    </DialogTitle>
                </DialogHeader>

                <div className="flex flex-wrap items-center gap-2 mb-3">
                    <div
                        data-testid="report-mode"
                        className="inline-flex bg-muted rounded-full p-1 text-xs font-bold uppercase tracking-widest"
                    >
                        {[
                            { id: "day", label: "Día" },
                            { id: "range", label: "Rango" },
                        ].map((m) => (
                            <button
                                key={m.id}
                                data-testid={`mode-${m.id}`}
                                onClick={() => setMode(m.id)}
                                className={`px-3 py-1.5 rounded-full ${
                                    mode === m.id
                                        ? "bg-foreground text-background"
                                        : "text-foreground/60"
                                }`}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>

                    {mode === "day" ? (
                        <Input
                            type="date"
                            value={date}
                            data-testid="report-date"
                            onChange={(e) => setDate(e.target.value)}
                            max={todayET()}
                            className="w-auto"
                        />
                    ) : (
                        <>
                            <Input
                                type="date"
                                value={fromDate}
                                data-testid="report-from"
                                onChange={(e) => setFromDate(e.target.value)}
                                max={todayET()}
                                className="w-auto"
                            />
                            <span className="text-xs text-muted-foreground">→</span>
                            <Input
                                type="date"
                                value={toDate}
                                data-testid="report-to"
                                onChange={(e) => setToDate(e.target.value)}
                                max={todayET()}
                                className="w-auto"
                            />
                            <div className="flex gap-1">
                                <button
                                    data-testid="preset-week"
                                    onClick={() => {
                                        const [f, t] = rangeThisWeek();
                                        setFromDate(f); setToDate(t);
                                    }}
                                    className="text-[10px] uppercase tracking-widest font-bold text-primary hover:underline"
                                >
                                    Esta semana
                                </button>
                                <span className="text-muted-foreground">·</span>
                                <button
                                    data-testid="preset-month"
                                    onClick={() => {
                                        const [f, t] = rangeThisMonth();
                                        setFromDate(f); setToDate(t);
                                    }}
                                    className="text-[10px] uppercase tracking-widest font-bold text-primary hover:underline"
                                >
                                    Este mes
                                </button>
                            </div>
                        </>
                    )}

                    <Button
                        data-testid="report-refresh"
                        onClick={() => (mode === "day" ? load(date) : loadRange(fromDate, toDate))}
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                    >
                        <ArrowsClockwise size={14} weight="bold" className="mr-1.5" /> Refrescar
                    </Button>
                    <div className="ml-auto flex gap-2">
                        <Button
                            data-testid="report-csv"
                            onClick={downloadCSV}
                            variant="outline"
                            size="sm"
                            className="rounded-full"
                        >
                            <DownloadSimple size={14} weight="bold" className="mr-1.5" /> CSV
                        </Button>
                        <Button
                            data-testid="report-print"
                            onClick={doPrint}
                            variant="outline"
                            size="sm"
                            className="rounded-full"
                        >
                            <Printer size={14} weight="bold" className="mr-1.5" /> Imprimir
                        </Button>
                    </div>
                </div>

                {loading && !report && !rangeReport ? (
                    <p className="text-center text-sm text-muted-foreground py-12">Cargando…</p>
                ) : mode === "range" && rangeReport ? (
                    <div id="daily-report-printable" className="space-y-4">
                        {/* Range channel cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {["cash", "card", "online"].map((k) => {
                                const cfg = CH[k];
                                const Icon = cfg.icon;
                                const s = rangeReport.summary[k];
                                return (
                                    <div
                                        key={k}
                                        data-testid={`range-${k}`}
                                        className={`rounded-2xl border border-border bg-gradient-to-br ${cfg.gradient} p-4`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className={`grid place-items-center h-10 w-10 rounded-full ${cfg.chip}`}>
                                                <Icon size={20} weight="fill" />
                                            </span>
                                            <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                                                {s.count} órdenes
                                            </span>
                                        </div>
                                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mt-3">
                                            {cfg.label}
                                        </p>
                                        <p className={`font-display font-black text-3xl ${cfg.text}`}>
                                            ${s.total.toFixed(2)}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Range grand total */}
                        <div className="rounded-2xl bg-foreground text-background p-4 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-[10px] uppercase tracking-widest opacity-70 font-bold truncate">
                                    {rangeReport.date_from} → {rangeReport.date_to}
                                </p>
                                <p className="font-display font-black text-3xl">
                                    ${rangeReport.grand.total.toFixed(2)}
                                </p>
                                {rangeReport.previous && (() => {
                                    const cur = rangeReport.grand.total;
                                    const prev = rangeReport.previous.grand.total;
                                    const diff = cur - prev;
                                    const pct = prev > 0 ? (diff / prev) * 100 : (cur > 0 ? 100 : 0);
                                    const up = diff >= 0;
                                    return (
                                        <p
                                            data-testid="vs-previous"
                                            className={`inline-flex items-center gap-1 mt-1 text-xs font-bold uppercase tracking-widest ${
                                                up ? "text-emerald-300" : "text-red-300"
                                            }`}
                                        >
                                            {up ? "▲" : "▼"} {up ? "+" : ""}${Math.abs(diff).toFixed(2)}
                                            {" · "}{up ? "+" : ""}{pct.toFixed(0)}%
                                            <span className="opacity-70">vs período anterior</span>
                                        </p>
                                    );
                                })()}
                            </div>
                            <div className="text-right shrink-0">
                                <p className="text-[10px] uppercase tracking-widest opacity-70 font-bold">
                                    {rangeReport.grand.count} órdenes · {rangeReport.days.length} días
                                </p>
                                <p className="text-sm opacity-80">
                                    Promedio ${(rangeReport.grand.total / Math.max(rangeReport.days.length, 1)).toFixed(2)}/día
                                </p>
                                {rangeReport.previous && (
                                    <p className="text-[10px] opacity-60 mt-1">
                                        Anterior: ${rangeReport.previous.grand.total.toFixed(2)}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Daily bar chart — visualizes each day's total so
                            the owner can see spikes/dips at a glance. */}
                        {rangeReport.days.length > 1 && (
                            <section
                                data-testid="range-chart"
                                className="rounded-2xl border border-border bg-white p-4"
                            >
                                <p className="font-display font-black text-lg mb-3">
                                    Ventas por día
                                </p>
                                <div className="h-52 w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart
                                            data={rangeReport.days.map((d, i) => ({
                                                label: new Date(d.date + "T12:00:00").toLocaleDateString(undefined, {
                                                    weekday: "short",
                                                    day: "numeric",
                                                }),
                                                Efectivo: d.cash,
                                                Tarjeta: d.card,
                                                Online: d.online,
                                                total: d.total,
                                                previous: rangeReport.previous?.days?.[i]?.total ?? null,
                                            }))}
                                            margin={{ top: 10, right: 8, left: 0, bottom: 0 }}
                                        >
                                            <CartesianGrid stroke="#eee" strokeDasharray="3 3" vertical={false} />
                                            <XAxis
                                                dataKey="label"
                                                tick={{ fontSize: 11, fill: "#666" }}
                                                tickLine={false}
                                                axisLine={false}
                                            />
                                            <YAxis
                                                tick={{ fontSize: 11, fill: "#666" }}
                                                tickLine={false}
                                                axisLine={false}
                                                tickFormatter={(v) => `$${v}`}
                                                width={44}
                                            />
                                            <Tooltip
                                                formatter={(v, name) => [`$${Number(v).toFixed(2)}`, name === "previous" ? "Período anterior" : name]}
                                                cursor={{ fill: "rgba(0,0,0,0.04)" }}
                                                contentStyle={{
                                                    borderRadius: 12,
                                                    border: "1px solid #e5e5e5",
                                                    fontSize: 12,
                                                }}
                                            />
                                            <Bar dataKey="Efectivo" stackId="a" fill="#10b981" />
                                            <Bar dataKey="Tarjeta"  stackId="a" fill="#3b82f6" />
                                            <Bar dataKey="Online"   stackId="a" fill="#E87811" radius={[6, 6, 0, 0]} />
                                            {rangeReport.previous && (
                                                <Line
                                                    type="monotone"
                                                    dataKey="previous"
                                                    stroke="#71717a"
                                                    strokeDasharray="4 3"
                                                    strokeWidth={2}
                                                    dot={{ r: 3, fill: "#71717a" }}
                                                />
                                            )}
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="flex justify-center gap-4 mt-2 text-[11px] uppercase tracking-widest font-bold flex-wrap">
                                    <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" />Efectivo</span>
                                    <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-blue-500" />Tarjeta</span>
                                    <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-primary" />Online</span>
                                    {rangeReport.previous && (
                                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                                            <span className="w-4 border-t-2 border-dashed border-zinc-500" />
                                            Anterior
                                        </span>
                                    )}
                                </div>
                            </section>
                        )}

                        {/* Daily breakdown table */}
                        <section className="rounded-2xl border border-border bg-white overflow-hidden">
                            <p className="font-display font-black text-lg px-4 py-3 border-b border-border">
                                Desglose diario
                            </p>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="range-table">
                                    <thead className="bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground">
                                        <tr className="text-left">
                                            <th className="px-4 py-2">Fecha</th>
                                            <th className="px-4 py-2 text-right">Órdenes</th>
                                            <th className="px-4 py-2 text-right">Efectivo</th>
                                            <th className="px-4 py-2 text-right">Tarjeta</th>
                                            <th className="px-4 py-2 text-right">Online</th>
                                            <th className="px-4 py-2 text-right">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rangeReport.days.map((d) => (
                                            <tr key={d.date} className="border-t border-border">
                                                <td className="px-4 py-2 font-semibold whitespace-nowrap">
                                                    {new Date(d.date + "T12:00:00").toLocaleDateString(undefined, {
                                                        weekday: "short",
                                                        month: "short",
                                                        day: "numeric",
                                                    })}
                                                </td>
                                                <td className="px-4 py-2 text-right">{d.count}</td>
                                                <td className="px-4 py-2 text-right text-emerald-700">${d.cash.toFixed(2)}</td>
                                                <td className="px-4 py-2 text-right text-blue-700">${d.card.toFixed(2)}</td>
                                                <td className="px-4 py-2 text-right text-primary">${d.online.toFixed(2)}</td>
                                                <td className="px-4 py-2 text-right font-bold">${d.total.toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </div>
                ) : mode === "day" && report && (
                    <div id="daily-report-printable" className="space-y-4">
                        {/* Channel cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {["cash", "card", "online"].map((k) => {
                                const cfg = CH[k];
                                const Icon = cfg.icon;
                                const s = report.summary[k];
                                return (
                                    <div
                                        key={k}
                                        data-testid={`report-${k}`}
                                        className={`rounded-2xl border border-border bg-gradient-to-br ${cfg.gradient} p-4`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className={`grid place-items-center h-10 w-10 rounded-full ${cfg.chip}`}>
                                                <Icon size={20} weight="fill" />
                                            </span>
                                            <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                                                {s.count} {s.count === 1 ? "orden" : "órdenes"}
                                            </span>
                                        </div>
                                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mt-3">
                                            {cfg.label}
                                        </p>
                                        <p className={`font-display font-black text-3xl ${cfg.text}`}>
                                            ${s.total.toFixed(2)}
                                        </p>
                                        <p className="text-[10px] text-muted-foreground mt-1">
                                            Subtotal ${s.subtotal.toFixed(2)} · Tax ${s.tax.toFixed(2)}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Grand total */}
                        <div className="rounded-2xl bg-foreground text-background p-4 flex items-center justify-between">
                            <div>
                                <p className="text-[10px] uppercase tracking-widest opacity-70 font-bold">
                                    Total del día · {report.date}
                                </p>
                                <p className="font-display font-black text-3xl">
                                    ${report.grand.total.toFixed(2)}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-[10px] uppercase tracking-widest opacity-70 font-bold">
                                    {report.grand.count} órdenes
                                </p>
                                <p className="text-sm opacity-80">
                                    Impuesto ${report.grand.tax.toFixed(2)}
                                </p>
                            </div>
                        </div>

                        {/* Cash reconciliation */}
                        <section
                            data-testid="cash-close-form"
                            className="rounded-2xl border border-border bg-white p-4"
                        >
                            <p className="font-display font-black text-xl mb-3">
                                Cuadre de caja
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <FormField
                                    label="Efectivo inicial (fondo)"
                                    testId="opening-cash"
                                    value={opening}
                                    onChange={setOpening}
                                />
                                <FormField
                                    label="Salidas de efectivo (payouts)"
                                    testId="payouts"
                                    value={payouts}
                                    onChange={setPayouts}
                                />
                                <div className="bg-muted rounded-xl px-3 py-2">
                                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                                        Ventas en efectivo (del sistema)
                                    </p>
                                    <p className="font-display font-black text-2xl text-emerald-700">
                                        ${(report.summary.cash.total).toFixed(2)}
                                    </p>
                                </div>
                                <div className="bg-muted rounded-xl px-3 py-2">
                                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                                        Efectivo esperado en gaveta
                                    </p>
                                    <p className="font-display font-black text-2xl">
                                        ${expected.toFixed(2)}
                                    </p>
                                </div>
                                <FormField
                                    label="Efectivo contado en gaveta"
                                    testId="counted-cash"
                                    value={counted}
                                    onChange={setCounted}
                                />
                                <div
                                    className={`rounded-xl px-3 py-2 border-2 ${
                                        Math.abs(variance) < 0.01
                                            ? "border-emerald-300 bg-emerald-50"
                                            : variance > 0
                                            ? "border-blue-300 bg-blue-50"
                                            : "border-red-300 bg-red-50"
                                    }`}
                                >
                                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold inline-flex items-center gap-1">
                                        {Math.abs(variance) < 0.01
                                            ? <CheckCircle size={12} weight="fill" className="text-emerald-600" />
                                            : <WarningCircle size={12} weight="fill" className={variance > 0 ? "text-blue-600" : "text-red-600"} />}
                                        Diferencia
                                    </p>
                                    <p
                                        data-testid="variance"
                                        className={`font-display font-black text-2xl ${
                                            Math.abs(variance) < 0.01
                                                ? "text-emerald-700"
                                                : variance > 0
                                                ? "text-blue-700"
                                                : "text-red-700"
                                        }`}
                                    >
                                        {variance >= 0 ? "+" : "−"}${Math.abs(variance).toFixed(2)}
                                    </p>
                                </div>
                            </div>
                            <textarea
                                data-testid="close-notes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Notas / observaciones (opcional)"
                                className="w-full mt-3 rounded-xl border border-input bg-background px-3 py-2 text-sm min-h-[70px]"
                            />

                            {report.latest_close && (
                                <p className="text-[11px] text-muted-foreground italic mt-2">
                                    Último cierre: {new Date(report.latest_close.closed_at).toLocaleString()} por {report.latest_close.closed_by}
                                </p>
                            )}

                            <div className="flex gap-2 mt-3">
                                <Button
                                    variant="outline"
                                    onClick={onClose}
                                    className="rounded-full flex-1"
                                >
                                    <X size={16} weight="bold" className="mr-1.5" />
                                    Cerrar
                                </Button>
                                <Button
                                    data-testid="save-close"
                                    onClick={close}
                                    disabled={closing}
                                    className="rounded-full flex-1 bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest"
                                >
                                    <CheckCircle size={16} weight="fill" className="mr-1.5" />
                                    {closing ? "Guardando…" : "Cerrar caja"}
                                </Button>
                            </div>
                        </section>

                        {/* By cashier */}
                        {Object.keys(report.by_cashier || {}).length > 0 && (
                            <section className="rounded-2xl border border-border bg-white p-4">
                                <p className="font-display font-black text-lg mb-2">Por cajero</p>
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                                            <th className="py-1">Cajero</th>
                                            <th className="py-1 text-right">Órdenes</th>
                                            <th className="py-1 text-right">Efectivo</th>
                                            <th className="py-1 text-right">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(report.by_cashier).map(([email, row]) => (
                                            <tr key={email} className="border-t border-border">
                                                <td className="py-1.5 font-semibold">{email}</td>
                                                <td className="py-1.5 text-right">{row.count}</td>
                                                <td className="py-1.5 text-right">${row.cash.toFixed(2)}</td>
                                                <td className="py-1.5 text-right font-bold">${row.total.toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </section>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function FormField({ label, value, onChange, testId }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                {label}
            </span>
            <Input
                data-testid={testId}
                type="number"
                step="0.01"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1"
            />
        </label>
    );
}
